import { useState, FormEvent } from 'react';
import { useInView } from '../../hooks/useInView';

interface FormState {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

interface FinalCTASectionProps {
  headline?: string;
  sub?: string;
  pageTitle?: string;
}

export default function FinalCTASection({
  headline = 'Experience a Higher Standard of Healing',
  sub = 'Personalized care. Intentional results. A deeper level of well-being.',
  pageTitle = 'Schedule Your Session',
}: FinalCTASectionProps) {
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', service: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const { ref, inView } = useInView();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setStatus('submitting');
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d73u77j41deorl02eiig', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', service: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      {/* Hero CTA Banner */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/7c0cdf2f273abacb4d1864490a0cad5f.jpg"
            alt="Khalsa Healing – Experience a Higher Standard"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#080808]/70 via-[#080808]/80 to-[#080808]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/30 to-transparent" />

        <div className="relative z-10 max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="w-12 h-px bg-[#d4af37]/40" />
            <span className="text-[#d4af37] text-base">✦</span>
            <span className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2
            className="text-4xl md:text-5xl lg:text-6xl text-white font-light leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {headline}
          </h2>
          <p className="text-[#9a9a9a] text-base mb-12 font-light" style={{ fontFamily: 'Inter, sans-serif' }}>
            {sub}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#booking"
              onClick={(e) => { e.preventDefault(); document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="inline-flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#c09b2a] text-[#080808] px-12 py-4 rounded-full text-xs uppercase tracking-[0.2em] font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Book Your Session Today
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </a>
            <a
              href="tel:+12153210305"
              className="inline-flex items-center justify-center gap-2 border border-[#d4af37]/30 hover:border-[#d4af37] text-[#d4af37] px-10 py-4 rounded-full text-xs uppercase tracking-[0.15em] transition-all duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              Call 215-321-0305
            </a>
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section
        id="booking"
        ref={ref as React.RefObject<HTMLElement>}
        className="bg-[#080808] py-32 border-t border-[#d4af37]/8"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 items-start">
            {/* Left: Context */}
            <div className={`lg:col-span-2 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="flex items-center gap-4 mb-5">
                <span className="w-8 h-px bg-[#d4af37]/30" />
                <p className="text-[#d4af37] uppercase tracking-[0.3em] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Ready to Begin?
                </p>
              </div>
              <h2
                className="text-4xl md:text-5xl text-white font-light leading-tight mb-6"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {pageTitle}
              </h2>
              <p className="text-[#5a5a5a] text-sm leading-relaxed mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>
                Fill out the form and we&apos;ll reach out within 24 hours to confirm your appointment and answer any questions.
              </p>

              <div className="space-y-6">
                {[
                  { icon: 'ri-map-pin-line', text: '90 West Afton Ave. #204, Yardley, PA 19067' },
                  { icon: 'ri-phone-line', text: '215-321-0305', href: 'tel:+12153210305' },
                  { icon: 'ri-mail-line', text: 'events@khalsahealingarts.com', href: 'mailto:events@khalsahealingarts.com' },
                  { icon: 'ri-time-line', text: 'Mon–Fri: 9am–6pm · By Appointment' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#d4af37]/8 flex-shrink-0">
                      <i className={`${item.icon} text-[#d4af37] text-sm`} />
                    </div>
                    {item.href ? (
                      <a href={item.href} className="text-[#5a5a5a] hover:text-[#d4af37] text-sm transition-colors cursor-pointer" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.text}
                      </a>
                    ) : (
                      <span className="text-[#5a5a5a] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {item.text}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              {/* Mobile CTA */}
              <div className="mt-10 lg:hidden">
                <a
                  href="tel:+12153210305"
                  className="flex items-center justify-center gap-2 bg-[#d4af37] text-[#080808] py-4 rounded-full text-sm font-semibold uppercase tracking-widest cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <i className="ri-phone-line text-base" />
                  Tap to Call Now
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className={`lg:col-span-3 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: '150ms' }}>
              {status === 'success' ? (
                <div className="bg-[#0f0f0f] border border-[#d4af37]/20 rounded-2xl p-14 text-center">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-[#d4af37]/10 rounded-full">
                    <i className="ri-check-line text-[#d4af37] text-2xl" />
                  </div>
                  <h3 className="text-white text-3xl mb-3 font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Request Received
                  </h3>
                  <p className="text-[#5a5a5a] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Thank you for reaching out. We&apos;ll be in touch within 24 hours to confirm your session.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="book-session-form"
                  onSubmit={handleSubmit}
                  className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-2xl p-8 md:p-10 space-y-5"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#5a5a5a] text-[10px] uppercase tracking-[0.2em] mb-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="w-full bg-[#151515] border border-[#1e1e1e] focus:border-[#d4af37]/40 rounded-lg px-4 py-3.5 text-white text-sm outline-none transition-colors placeholder-[#2a2a2a]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label className="block text-[#5a5a5a] text-[10px] uppercase tracking-[0.2em] mb-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="w-full bg-[#151515] border border-[#1e1e1e] focus:border-[#d4af37]/40 rounded-lg px-4 py-3.5 text-white text-sm outline-none transition-colors placeholder-[#2a2a2a]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-[#5a5a5a] text-[10px] uppercase tracking-[0.2em] mb-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="w-full bg-[#151515] border border-[#1e1e1e] focus:border-[#d4af37]/40 rounded-lg px-4 py-3.5 text-white text-sm outline-none transition-colors placeholder-[#2a2a2a]"
                        style={{ fontFamily: 'Inter, sans-serif' }}
                        placeholder="+1 (215) ..."
                      />
                    </div>
                    <div>
                      <label className="block text-[#5a5a5a] text-[10px] uppercase tracking-[0.2em] mb-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                        Service of Interest
                      </label>
                      <select
                        name="service"
                        value={form.service}
                        onChange={(e) => setForm({ ...form, service: e.target.value })}
                        className="w-full bg-[#151515] border border-[#1e1e1e] focus:border-[#d4af37]/40 rounded-lg px-4 py-3.5 text-sm outline-none transition-colors cursor-pointer"
                        style={{ fontFamily: 'Inter, sans-serif', color: form.service ? '#fff' : '#2a2a2a' }}
                      >
                        <option value="" style={{ color: '#2a2a2a', background: '#151515' }}>Select a service</option>
                        <option value="Holistic Healing Therapy" style={{ color: '#fff', background: '#151515' }}>Holistic Healing Therapy</option>
                        <option value="Energy Work & Bodywork" style={{ color: '#fff', background: '#151515' }}>Energy Work &amp; Bodywork</option>
                        <option value="Trauma Release & Nervous System Reset" style={{ color: '#fff', background: '#151515' }}>Trauma Release &amp; Nervous System Reset</option>
                        <option value="Personalized Healing Session" style={{ color: '#fff', background: '#151515' }}>Personalized Healing Session</option>
                        <option value="Not Sure / General Inquiry" style={{ color: '#fff', background: '#151515' }}>Not Sure — General Inquiry</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[#5a5a5a] text-[10px] uppercase tracking-[0.2em] mb-2.5" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Goals &amp; Concerns
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      maxLength={500}
                      rows={4}
                      className="w-full bg-[#151515] border border-[#1e1e1e] focus:border-[#d4af37]/40 rounded-lg px-4 py-3.5 text-white text-sm outline-none transition-colors placeholder-[#2a2a2a] resize-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                      placeholder="Share what you're experiencing or what you hope to achieve..."
                    />
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-[#2a2a2a] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>Max 500 characters</span>
                      <span className="text-[#2a2a2a] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>{form.message.length}/500</span>
                    </div>
                    {form.message.length > 500 && (
                      <p className="text-red-400 text-xs mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>Please keep your message under 500 characters.</p>
                    )}
                  </div>
                  {status === 'error' && (
                    <p className="text-red-400 text-sm text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                      Something went wrong. Please try again or call 215-321-0305.
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={status === 'submitting' || form.message.length > 500}
                    className="w-full bg-[#d4af37] hover:bg-[#c09b2a] disabled:bg-[#d4af37]/40 text-[#080808] py-4.5 rounded-full text-xs uppercase tracking-[0.2em] transition-all duration-300 cursor-pointer font-semibold whitespace-nowrap"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {status === 'submitting' ? 'Sending Your Request...' : 'Request Your Session'}
                  </button>
                  <p className="text-center text-[#3a3a3a] text-[10px]" style={{ fontFamily: 'Inter, sans-serif' }}>
                    We respond within 24 hours · Your information is private and secure
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
